import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaCode, FaMarkdown, FaFileExport, FaCheck, FaArrowRight, FaUsers, FaRocket, FaDesktop, FaCloud, FaApple, FaWindows, FaLinux, FaBrain, FaMagic, FaLightbulb, FaLanguage, FaSpellCheck } from "react-icons/fa";
import { QRCodeSVG } from 'qrcode.react';
import MonoButton from "../kits/DesignKit/MonoButton";
import PaperDarkBG from "../kits/DesignKit/PaperDarkBG";
import BookingModal from "./BookingModal";
import SeoHelmet from "./SeoHelmet";

function ZenEditorLandingPage() {
  const [showBooking, setShowBooking] = useState(false);
  const navigate = useNavigate();

  // Analytics tracking for CTA clicks
  const trackCTA = (ctaName) => {
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'ZenEditor',
        event_label: ctaName,
      });
    }
    console.log(`CTA Clicked: ${ctaName}`);
  };

  const features = [
    {
      icon: <FaBrain className="text-brand text-2xl" />,
      title: "KI-Powered Desktop App",
      description: "Integrierte KI direkt in der Desktop-App. Schreibe schneller, besser, smarter - komplett offline."
    },
    {
      icon: <FaCode className="text-brand text-2xl" />,
      title: "EditorJS + Markdown",
      description: "Der einzige Editor, der beides kann. Wechsle fließend zwischen EditorJS-Blöcken und Markdown."
    },
    {
      icon: <FaFileExport className="text-brand text-2xl" />,
      title: "Portabel & Flexibel",
      description: "Export in Markdown, JSON oder HTML. Deine Inhalte gehören dir, nicht dem Editor."
    }
  ];

  const aiFeatures = [
    {
      icon: <FaMagic className="text-purple-600 text-2xl" />,
      title: "Text verbessern",
      description: "KI optimiert deinen Text: Grammatik, Stil, Klarheit. Auf Knopfdruck."
    },
    {
      icon: <FaLightbulb className="text-yellow-500 text-2xl" />,
      title: "Content-Ideen",
      description: "Blockiert? Die KI schlägt Headlines, Absätze und Strukturen vor."
    },
    {
      icon: <FaLanguage className="text-blue-600 text-2xl" />,
      title: "Übersetzen",
      description: "Übersetze Texte in 50+ Sprachen. Direkt im Editor, ohne Copy-Paste."
    },
    {
      icon: <FaSpellCheck className="text-green-600 text-2xl" />,
      title: "Zusammenfassen",
      description: "Lange Texte? KI erstellt präzise Zusammenfassungen in Sekunden."
    }
  ];

  const platformFeatures = [
    {
      icon: <FaDesktop className="text-brand text-3xl" />,
      title: "Desktop App",
      subtitle: "macOS, Windows, Linux + KI",
      features: [
        "✨ Integrierte KI (lokal & offline)",
        "Native Performance",
        "Offline-First",
        "Lokale Datenspeicherung",
        "Systemintegration",
        "Keine Internetverbindung nötig",
        "Volle Privatsphäre"
      ],
      cta: "Desktop App herunterladen",
      highlight: true
    },
    {
      icon: <FaCloud className="text-brand text-3xl" />,
      title: "Web Version",
      subtitle: "Browser-basiert",
      features: [
        "Überall verfügbar",
        "Cloud-Sync",
        "Team-Kollaboration",
        "Automatische Updates",
        "Kein Download nötig",
        "Cross-Device"
      ],
      cta: "Web-Version testen",
      highlight: false
    }
  ];

  const useCases = [
    {
      title: "Für Content Creator",
      description: "KI hilft dir beim Schreiben von Blog-Posts, Social Media Content und Artikeln. Von der Idee bis zum fertigen Text.",
      audience: "Blogger, Freelance Writer, Content Creator",
      icon: <FaMagic className="text-purple-600" />
    },
    {
      title: "Für Agenturen",
      description: "Content-Teams arbeiten mit KI-Unterstützung 3x schneller. EditorJS für Landing Pages, Markdown für Blogs, KI für alles.",
      audience: "Marketing-Agenturen, Content-Teams",
      icon: <FaUsers className="text-brand" />
    },
    {
      title: "Für Tech Writers",
      description: "Dokumentation schreiben mit KI-Unterstützung. Technische Texte klar und verständlich - automatisch.",
      audience: "Technical Writers, Developer Relations",
      icon: <FaCode className="text-blue-600" />
    },
    {
      title: "Für Mehrsprachige Teams",
      description: "Schreibe auf Deutsch, übersetze mit KI in 50+ Sprachen. Perfekt für internationale Projekte.",
      audience: "International Teams, Export-Unternehmen",
      icon: <FaLanguage className="text-green-600" />
    }
  ];

  const pricingPlans = [
    {
      name: "Desktop",
      price: "Kostenlos",
      period: "",
      description: "Die KI-powered Desktop-App",
      features: [
        "🤖 KI-Assistent integriert",
        "Desktop App (Mac, Win, Linux)",
        "Unbegrenzte Dokumente",
        "EditorJS + Markdown",
        "Export (MD, JSON, HTML)",
        "Offline-First",
        "100% Privat & Lokal",
        "Keine Registrierung nötig",
        "Community Support"
      ],
      cta: "Jetzt herunterladen",
      highlight: false,
      platform: "desktop"
    },
    {
      name: "Pro",
      price: "29€",
      period: "/Monat",
      description: "Desktop + Web + Cloud + KI Pro",
      features: [
        "🚀 Erweiterte KI-Features",
        "Desktop App + Web Version",
        "Bis zu 5 Benutzer",
        "Cloud-Sync & Backup",
        "Versionierung",
        "Kollaboration Echtzeit",
        "Custom Blocks",
        "Alle Export-Formate",
        "Priority Support"
      ],
      cta: "Pro testen",
      highlight: true,
      platform: "hybrid"
    },
    {
      name: "Agency",
      price: "149€",
      period: "/Monat",
      description: "Für Teams & Agenturen",
      features: [
        "Unbegrenzte Benutzer",
        "Alle Pro Features",
        "White-Label Option",
        "API-Zugriff",
        "Custom Integration",
        "Dedizierter Support",
        "SLA-Garantie",
        "On-Premise Option"
      ],
      cta: "Kontakt aufnehmen",
      highlight: false,
      platform: "enterprise"
    }
  ];

  const testimonials = [
    {
      quote: "ZenEditor hat unseren Content-Workflow revolutioniert. EditorJS für Landing Pages, Markdown für Blogs – alles in einem Tool.",
      author: "Max Mustermann",
      role: "Head of Content, TechStartup GmbH"
    },
    {
      quote: "Endlich ein Editor, der mich nicht einschränkt. Ich kann schreiben wie ich will und bekomme trotzdem strukturierte Outputs.",
      author: "Sarah Schmidt",
      role: "Freelance Writer"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SeoHelmet
        title="ZenEditor – KI-Powered Content Editor | EditorJS + Markdown + AI"
        description="Der erste Content Editor mit integrierter KI. EditorJS + Markdown + AI-Assistent in einem Tool. Desktop App kostenlos. Schreibe schneller, besser, smarter."
        url="https://www.denisbitter.de/zeneditor"
        image="https://www.denisbitter.de/images/zeneditor-og.jpg"
        keywords="ai content editor, ki editor, markdown editor, editorjs, hybrid editor, desktop app, tauri app, ai writing assistant, content management, technical writing"
      />

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 py-20 text-center">
        <div className="mb-6 flex justify-center">
          <span className="bg-gradient-to-r from-brand to-purple-600 text-white px-4 py-2 rounded-full text-xs font-semibold font-mono flex items-center gap-2">
            <FaBrain /> Der KI-Powered Content Editor
          </span>
        </div>

        <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900">
          EditorJS <span className="text-brand">+</span> Markdown <span className="text-purple-600">+ KI</span>
          <br />
          <span className="text-gray-600">Schreibe mit künstlicher Intelligenz.</span>
        </h1>

        <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Der erste Content Editor mit integriertem KI-Assistenten.
          EditorJS-Blöcke, Markdown und KI - alles in einem Tool.
          <strong className="text-gray-900"> Desktop App kostenlos.</strong>
        </p>

        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
          <div className="flex flex-col items-center gap-3">
            <MonoButton
              onClick={() => {
                trackCTA('Hero_Desktop_Download');
                window.open('https://github.com/YOURUSERNAME/zeneditor/releases', '_blank');
              }}
              className="px-8 py-3 text-sm flex items-center gap-2 bg-brand text-white"
            >
              <FaDesktop /> Desktop App herunterladen
            </MonoButton>

            {/* QR Code für GitHub Download */}
            <div className="hidden sm:flex flex-col items-center bg-white p-3 rounded-lg shadow-md border-2 border-brand w-fit mx-auto">
              <QRCodeSVG
                value="https://github.com/YOURUSERNAME/zeneditor/releases"
                size={100}
                level="M"
              />
              <p className="text-xs text-gray-600 text-center mt-2 font-mono">Scan für Download</p>
            </div>
          </div>

          <MonoButton
            onClick={() => {
              trackCTA('Hero_Live_Demo');
              navigate("/dashboard?demo=zeneditor");
            }}
            className="px-8 py-3 text-sm flex items-center gap-2"
          >
            Live Demo ausprobieren <FaArrowRight />
          </MonoButton>
          <MonoButton
            onClick={() => {
              trackCTA('Hero_Beratung');
              setShowBooking(true);
            }}
            className="px-8 py-3 text-sm bg-white border-2 border-gray-900 hover:bg-gray-50"
          >
            Beratungsgespräch buchen
          </MonoButton>
        </div>

        <div className="mt-8 flex items-center justify-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <FaApple /> macOS
          </div>
          <div className="flex items-center gap-1">
            <FaWindows /> Windows
          </div>
          <div className="flex items-center gap-1">
            <FaLinux /> Linux
          </div>
        </div>

        <div className="mt-6 text-xs text-gray-500 font-mono">
          ✓ Desktop App kostenlos · ✓ Pro 14 Tage testen · ✓ Keine Kreditkarte nötig
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <PaperDarkBG className="rounded-lg p-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4 text-white">
              Das Problem: Du musst dich entscheiden.
            </h2>
            <p className="text-gray-300 mb-6">
              EditorJS ist perfekt für strukturierte, blockbasierte Inhalte.
              Markdown ist unschlagbar für schnelles, fokussiertes Schreiben.
              <br /><br />
              <strong className="text-brand">Warum nicht beides?</strong>
            </p>
            <div className="bg-white/5 border border-white/10 rounded-lg p-6 text-left">
              <p className="text-sm text-gray-300 mb-2">
                <span className="text-red-400">❌</span> Andere Editoren zwingen dich in eine Richtung
              </p>
              <p className="text-sm text-gray-300 mb-2">
                <span className="text-red-400">❌</span> Vendor Lock-in: Deine Inhalte sind gefangen
              </p>
              <p className="text-sm text-gray-300">
                <span className="text-red-400">❌</span> Export-Chaos: Jedes Tool sein eigenes Format
              </p>
            </div>
          </div>
        </PaperDarkBG>
      </section>

      {/* Solution Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">
            Die Lösung: <span className="text-brand">ZenEditor</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Ein Editor. Zwei Modi. Unendliche Möglichkeiten.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-brand/10 rounded-lg flex items-center justify-center mb-4">
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-900">{feature.title}</h3>
              <p className="text-sm text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AI Features Section */}
      <section className="bg-gradient-to-br from-purple-50 via-white to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="mb-4 flex justify-center">
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2">
                <FaBrain /> KI-Powered
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Dein persönlicher KI-Schreibassistent
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Integrierte künstliche Intelligenz direkt in der Desktop-App.
              Schreibe schneller, besser und kreativer - <strong>komplett offline</strong>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {aiFeatures.map((feature, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-all hover:scale-105">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 md:p-12 text-center text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-brand">
              KI lokal auf deinem Gerät
            </h3>
            <p className="text-lg mb-6 max-w-2xl mx-auto opacity-90">
              Keine Cloud. Keine Datenübertragung. Die KI läuft direkt auf deinem Computer.
              Deine Texte bleiben privat. Immer.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <FaCheck /> 100% Offline
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <FaCheck /> Keine API-Kosten
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <FaCheck /> Volle Privatsphäre
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
                <FaCheck /> Unbegrenzte Nutzung
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Section - Desktop vs. Web */}
      <section className="max-w-7xl mx-auto px-4 py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">
            Wähle deine Plattform
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Desktop App für maximale Privatsphäre. Web-Version für Team-Kollaboration. Oder beides.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {platformFeatures.map((platform, i) => (
            <div
              key={i}
              className={`bg-white border-2 rounded-lg p-8 ${
                platform.highlight
                  ? "border-brand shadow-xl"
                  : "border-gray-200"
              }`}
            >
              {platform.highlight && (
                <div className="bg-brand text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                  EMPFOHLEN FÜR STARTER
                </div>
              )}

              <div className="w-16 h-16 bg-brand/10 rounded-lg flex items-center justify-center mb-4">
                {platform.icon}
              </div>

              <h3 className="text-2xl font-bold mb-2 text-gray-900">{platform.title}</h3>
              <p className="text-sm text-gray-600 mb-6">{platform.subtitle}</p>

              <div className="space-y-3 mb-6">
                {platform.features.map((feature, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <FaCheck className="text-brand flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <MonoButton
                onClick={() => {
                  trackCTA(`Platform_${platform.title}_${platform.cta.replace(/\s+/g, '_')}`);
                  if (platform.title === "Desktop App") {
                    window.open('https://github.com/YOURUSERNAME/zeneditor/releases', '_blank');
                  } else {
                    navigate("/dashboard?demo=zeneditor");
                  }
                }}
                className={`w-full py-3 text-sm ${
                  platform.highlight
                    ? "bg-brand text-white"
                    : "bg-gray-900 text-white"
                }`}
              >
                {platform.cta}
              </MonoButton>
            </div>
          ))}
        </div>
      </section>

      {/* Live Demo Section */}
      <section className="bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">
            Sieh es selbst in Aktion
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Keine Installation nötig. Probiere den ZenEditor direkt im Browser aus.
          </p>
          <MonoButton
            onClick={() => {
              trackCTA('Live_Demo_Section');
              navigate("/dashboard?demo=zeneditor");
            }}
            className="px-8 py-3 text-sm flex items-center gap-2 mx-auto"
          >
            <FaRocket /> Zur interaktiven Demo
          </MonoButton>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">
            Wer nutzt ZenEditor?
          </h2>
          <p className="text-gray-600">
            Von Freelancern bis Enterprise – ZenEditor skaliert mit dir.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {useCases.map((useCase, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center text-2xl">
                  {useCase.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-2 text-gray-900">{useCase.title}</h3>
                  <p className="text-sm text-gray-700 mb-3">{useCase.description}</p>
                  <div className="text-xs text-brand font-semibold">{useCase.audience}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="max-w-7xl mx-auto px-4 py-16" id="pricing">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">
            Einfache, transparente Preise
          </h2>
          <p className="text-gray-600">
            Alle Pläne inkl. 14 Tage kostenlos testen. Keine Kreditkarte nötig.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, i) => (
            <div
              key={i}
              className={`bg-white border-2 rounded-lg p-8 ${
                plan.highlight
                  ? "border-brand shadow-xl scale-105"
                  : "border-gray-200"
              }`}
            >
              {plan.highlight && (
                <div className="bg-brand text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                  BELIEBTESTE WAHL
                </div>
              )}

              <h3 className="text-xl font-bold mb-2 text-gray-900">{plan.name}</h3>
              <p className="text-sm text-gray-600 mb-4">{plan.description}</p>

              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                <span className="text-gray-600">{plan.period}</span>
              </div>

              <MonoButton
                onClick={() => {
                  trackCTA(`Pricing_${plan.name}_${plan.cta.replace(/\s+/g, '_')}`);
                  if (plan.platform === "desktop") {
                    window.open('https://github.com/YOURUSERNAME/zeneditor/releases', '_blank');
                  } else if (plan.platform === "enterprise") {
                    setShowBooking(true);
                  } else {
                    navigate("/dashboard?demo=zeneditor");
                  }
                }}
                className={`w-full py-3 text-sm mb-6 ${
                  plan.highlight
                    ? "bg-brand text-white"
                    : "bg-gray-900 text-white"
                }`}
              >
                {plan.cta}
              </MonoButton>

              <div className="space-y-3">
                {plan.features.map((feature, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <FaCheck className="text-brand mt-1 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-gray-600 mb-4">
            Brauchst du mehr? Custom Lösungen für Enterprise-Kunden verfügbar.
          </p>
          <button
            onClick={() => {
              trackCTA('Pricing_Enterprise_Contact');
              setShowBooking(true);
            }}
            className="text-brand underline hover:no-underline text-sm font-semibold"
          >
            Kontaktiere uns für individuelle Angebote
          </button>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">
              Was unsere Kunden sagen
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-8">
                <p className="text-gray-700 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-brand/10 rounded-full flex items-center justify-center">
                    <span className="text-brand font-bold text-lg">
                      {testimonial.author.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">
            Häufig gestellte Fragen
          </h2>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-bold text-gray-900 mb-2">Kann ich meine Inhalte exportieren?</h3>
            <p className="text-sm text-gray-700">
              Ja, absolut! ZenEditor unterstützt Export in Markdown, JSON und HTML.
              Deine Inhalte gehören dir – kein Vendor Lock-in.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-bold text-gray-900 mb-2">Welche Integrationen werden unterstützt?</h3>
            <p className="text-sm text-gray-700">
              ZenEditor bietet eine REST-API und Webhooks. Integriere mit deinem CMS,
              Notion, GitHub, oder jedem anderen Tool via API.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-bold text-gray-900 mb-2">Ist ZenEditor DSGVO-konform?</h3>
            <p className="text-sm text-gray-700">
              Ja. Alle Daten werden auf deutschen Servern gespeichert.
              Du hast volle Kontrolle über deine Daten. On-Premise-Option verfügbar.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="font-bold text-gray-900 mb-2">Kann ich Custom Blocks erstellen?</h3>
            <p className="text-sm text-gray-700">
              Ab dem Pro-Plan kannst du eigene EditorJS-Blocks erstellen und integrieren.
              Volle Flexibilität für deine Use Cases.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-gradient-to-r from-brand to-orange-600 py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">
            Bereit für den besten Content Editor?
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Starte heute kostenlos. Keine Kreditkarte. Kein Risiko.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <div className="flex flex-col items-center gap-4">
              <MonoButton
                onClick={() => {
                  trackCTA('Final_CTA_Desktop_Download');
                  window.open('https://github.com/YOURUSERNAME/zeneditor/releases', '_blank');
                }}
                className="px-8 py-3 text-sm bg-white text-gray-900 hover:bg-gray-100 flex items-center gap-2 justify-center"
              >
                <FaDesktop /> Desktop App herunterladen
              </MonoButton>

              {/* QR Code für GitHub Download */}
              <div className="hidden md:flex flex-col items-center bg-white p-4 rounded-xl shadow-lg w-fit mx-auto">
                <QRCodeSVG
                  value="https://github.com/YOURUSERNAME/zeneditor/releases"
                  size={120}
                  level="H"
                />
                <p className="text-xs text-gray-700 text-center mt-2 font-bold">
                  Scan mit dem Handy
                </p>
              </div>
            </div>

            <MonoButton
              onClick={() => {
                trackCTA('Final_CTA_Web_Test');
                navigate("/dashboard?demo=zeneditor");
              }}
              className="px-8 py-3 text-sm bg-gray-800 text-white hover:bg-gray-700"
            >
              Web-Version testen
            </MonoButton>
            <MonoButton
              onClick={() => {
                trackCTA('Final_CTA_Beratung');
                setShowBooking(true);
              }}
              className="px-8 py-3 text-sm bg-gray-900 text-white hover:bg-gray-800"
            >
              Beratungsgespräch
            </MonoButton>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold mb-4 text-sm">Produkt</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => {
                  trackCTA('Footer_Demo');
                  navigate("/dashboard?demo=zeneditor");
                }} className="hover:text-brand">Web Demo</button></li>
                <li><button onClick={() => {
                  trackCTA('Footer_Desktop_Download');
                  window.open('https://github.com/YOURUSERNAME/zeneditor/releases', '_blank');
                }} className="hover:text-brand">Desktop App</button></li>
                <li><a href="#pricing" className="hover:text-brand">Preise</a></li>
                <li><button onClick={() => {
                  trackCTA('Footer_Kontakt');
                  setShowBooking(true);
                }} className="hover:text-brand">Kontakt</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-sm">Ressourcen</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/zen" className="hover:text-brand">Blog</a></li>
                <li><a href="/datenschutz" className="hover:text-brand">Datenschutz</a></li>
                <li><a href="/impressum" className="hover:text-brand">Impressum</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-sm">Weitere Produkte</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => navigate("/dashboard?demo=lms")} className="hover:text-brand">LMS System</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-sm">Kontakt</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Denis Bitter</li>
                <li>saghallo@denisbitter.de</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-500">
            © 2026 Denis Bitter. Made with ❤️ in Germany.
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      {showBooking && <BookingModal onClose={() => setShowBooking(false)} />}
    </div>
  );
}

export default ZenEditorLandingPage;
